/** Componentes de apresentação reutilizados por todas as telas. */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { moeda, paraNumero, porcentagem } from '../utils/formato';
import { Icone } from './Icone';

// ── Campo de data ──────────────────────────────────────────────────────────
/** Data em `dd/mm/aaaa`, digitada, sem `<input type="date">`.
 *
 * O input nativo renderiza no locale do **navegador**: num Chrome em en-US a
 * mesma tela mostra `09/02/2026` para o dia 2 de setembro, e ninguém percebe
 * até o registro sair no mês errado. Aqui o formato é do componente, não do
 * navegador, e não muda de máquina para máquina.
 *
 * Fala ISO (`aaaa-mm-dd`) para fora: o transporte continua o de sempre e as
 * telas que já usavam data não precisam saber que a digitação mudou.
 */
export function CampoData({
  id,
  valor,
  aoMudar,
  desabilitado,
  obrigatorio,
  rotulo,
  estilo,
}: {
  id?: string;
  /** Data em ISO, ou vazio. */
  valor: string;
  aoMudar: (iso: string) => void;
  desabilitado?: boolean;
  obrigatorio?: boolean;
  /** `aria-label`, para campo sem `<label>` visível (linha de tabela). */
  rotulo?: string;
  estilo?: React.CSSProperties;
}) {
  const paraTexto = (iso: string) => {
    const partes = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return partes ? `${partes[3]}/${partes[2]}/${partes[1]}` : '';
  };

  const [texto, setTexto] = useState(() => paraTexto(valor));

  // Só reescreve quando a data muda por fora — a leitura da foto preenche o
  // campo. Sobrescrever a cada tecla impediria de digitar.
  useEffect(() => {
    setTexto((atual) => (paraData(atual) === valor ? atual : paraTexto(valor)));
  }, [valor]);

  const digitar = (bruto: string) => {
    const digitos = bruto.replace(/\D/g, '').slice(0, 8);
    const partes = [digitos.slice(0, 2), digitos.slice(2, 4), digitos.slice(4, 8)];
    const formatado = partes.filter(Boolean).join('/');
    setTexto(formatado);
    aoMudar(paraData(formatado));
  };

  return (
    <input
      id={id}
      value={texto}
      onChange={(e) => digitar(e.target.value)}
      disabled={desabilitado}
      required={obrigatorio}
      aria-label={rotulo}
      style={estilo}
      inputMode="numeric"
      placeholder="dd/mm/aaaa"
      maxLength={10}
      autoComplete="off"
    />
  );
}

/** `dd/mm/aaaa` → ISO, ou vazio se a data não existir no calendário. */
function paraData(texto: string): string {
  const partes = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(texto);
  if (!partes) return '';
  const [, dia, mes, ano] = partes;
  const quando = new Date(Number(ano), Number(mes) - 1, Number(dia));
  // `new Date(2026, 1, 31)` vira 3 de março sem reclamar: comparar de volta é
  // o que denuncia 31/02 e 30/02.
  if (
    quando.getFullYear() !== Number(ano) ||
    quando.getMonth() !== Number(mes) - 1 ||
    quando.getDate() !== Number(dia)
  ) {
    return '';
  }
  return `${ano}-${mes}-${dia}`;
}

/** Mês de competência em `mm/aaaa` — mesmo motivo do `CampoData`: o
 * `<input type="month">` nativo segue o idioma do navegador.
 * Fala `aaaa-mm` para fora e só avisa quando o mês digitado é válido. */
export function CampoMes({
  id,
  valor,
  aoMudar,
  desabilitado,
  obrigatorio,
}: {
  id?: string;
  /** Mês em `aaaa-mm`, ou vazio. */
  valor: string;
  aoMudar: (mes: string) => void;
  desabilitado?: boolean;
  obrigatorio?: boolean;
}) {
  const paraTexto = (mes: string) => {
    const partes = /^(\d{4})-(\d{2})/.exec(mes);
    return partes ? `${partes[2]}/${partes[1]}` : '';
  };
  const [texto, setTexto] = useState(() => paraTexto(valor));

  useEffect(() => {
    setTexto((atual) => (paraMes(atual) === valor.slice(0, 7) ? atual : paraTexto(valor)));
  }, [valor]);

  const digitar = (bruto: string) => {
    const digitos = bruto.replace(/\D/g, '').slice(0, 6);
    const formatado = [digitos.slice(0, 2), digitos.slice(2, 6)].filter(Boolean).join('/');
    setTexto(formatado);
    const mes = paraMes(formatado);
    if (mes) aoMudar(mes);
  };

  return (
    <input
      id={id}
      value={texto}
      onChange={(e) => digitar(e.target.value)}
      disabled={desabilitado}
      required={obrigatorio}
      inputMode="numeric"
      placeholder="mm/aaaa"
      maxLength={7}
      autoComplete="off"
    />
  );
}

function paraMes(texto: string): string {
  const partes = /^(\d{2})\/(\d{4})$/.exec(texto);
  if (!partes || Number(partes[1]) < 1 || Number(partes[1]) > 12) return '';
  return `${partes[2]}-${partes[1]}`;
}

/** Valor em reais com máscara pt-BR: digita-se só números e a vírgula anda
 * sozinha (2704000 → 27.040,00), como em caixa eletrônico.
 *
 * Antes o campo mostrava o decimal cru da API (`27040.00`) e quem digitava
 * `27.040,00` — o natural — mandava um número inválido. Fala o mesmo formato
 * de antes para fora (`27040.00`), então o envio dos formulários não muda. */
export function CampoMoeda({
  id,
  valor,
  aoMudar,
  desabilitado,
  obrigatorio,
  rotulo,
  estilo,
}: {
  id?: string;
  /** Decimal como a API manda (`"27040.00"`), número, ou vazio. */
  valor: string | number | null | undefined;
  aoMudar: (valor: string) => void;
  desabilitado?: boolean;
  obrigatorio?: boolean;
  rotulo?: string;
  estilo?: React.CSSProperties;
}) {
  const bruto = valor === null || valor === undefined ? '' : String(valor).trim();
  const numero = Number(bruto.replace(',', '.'));
  const texto =
    bruto === '' || !Number.isFinite(numero)
      ? ''
      : numero.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <input
      id={id}
      value={texto}
      onChange={(e) => {
        const digitos = e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
        aoMudar(digitos === '' ? '' : (Number(digitos) / 100).toFixed(2));
      }}
      disabled={desabilitado}
      required={obrigatorio}
      aria-label={rotulo}
      style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', ...estilo }}
      inputMode="numeric"
      placeholder="0,00"
      autoComplete="off"
    />
  );
}

// ── Cartão ─────────────────────────────────────────────────────────────────
export function Card({
  titulo,
  acao,
  children,
  compacto,
  estilo,
}: {
  titulo?: ReactNode;
  acao?: ReactNode;
  children: ReactNode;
  compacto?: boolean;
  estilo?: React.CSSProperties;
}) {
  return (
    <div className={`card${compacto ? ' compacto' : ''}`} style={estilo}>
      {titulo && (
        <div className="card-t">
          <span>{titulo}</span>
          {acao}
        </div>
      )}
      {children}
    </div>
  );
}

// ── KPI ────────────────────────────────────────────────────────────────────
type CorKpi = 'r' | 'g' | 'a' | 'b' | 'p' | undefined;

export function Kpi({
  rotulo,
  valor,
  detalhe,
  borda,
  cor,
  aoClicar,
}: {
  rotulo: ReactNode;
  valor: ReactNode;
  detalhe?: ReactNode;
  borda?: CorKpi;
  cor?: 'vr' | 'vg' | 'va' | 'vb' | 'vp';
  aoClicar?: () => void;
}) {
  const classe = ['kpi', borda ? `brd-${borda}` : '', aoClicar ? 'clicavel' : '']
    .filter(Boolean)
    .join(' ');
  const conteudo = (
    <>
      <div className="kpi-l">{rotulo}</div>
      <div className={`kpi-v ${cor ?? ''}`}>{valor}</div>
      {detalhe && <div className="kpi-s">{detalhe}</div>}
    </>
  );
  return aoClicar ? (
    <button type="button" className={classe} onClick={aoClicar}>
      {conteudo}
    </button>
  ) : (
    <div className={classe}>{conteudo}</div>
  );
}

export function GradeKpis({ children }: { children: ReactNode }) {
  return <div className="kpis">{children}</div>;
}

// ── Selo ───────────────────────────────────────────────────────────────────
export function Selo({ tipo, children }: { tipo: string; children: ReactNode }) {
  return <span className={`badge b-${tipo}`}>{children}</span>;
}

// ── Cabeçalho de página ────────────────────────────────────────────────────
export function TituloPagina({
  titulo,
  subtitulo,
  acoes,
}: {
  titulo: ReactNode;
  subtitulo?: ReactNode;
  /** Ação principal da tela (ex.: "+ Novo"), alinhada à direita do título. */
  acoes?: ReactNode;
}) {
  return (
    <header className="pg-cab">
      <div style={{ minWidth: 0 }}>
        <h1 className="pg-h">{titulo}</h1>
        {subtitulo && <div className="pg-s">{subtitulo}</div>}
      </div>
      {acoes && <div className="pg-acoes">{acoes}</div>}
    </header>
  );
}

// ── Estados de carga e erro ────────────────────────────────────────────────
export function Carregando({ altura = 180 }: { altura?: number }) {
  return (
    <div className="card">
      <div className="skeleton" style={{ height: 14, width: '35%', marginBottom: 12 }} />
      <div className="skeleton" style={{ height: altura }} />
    </div>
  );
}

export function Erro({ mensagem, aoTentarNovamente }: { mensagem: string; aoTentarNovamente?: () => void }) {
  return (
    <div className="card">
      <div className="aviso erro" style={{ marginBottom: aoTentarNovamente ? 10 : 0 }}>
        <Icone nome="alerta" tamanho={15} /> {mensagem}
      </div>
      {aoTentarNovamente && (
        <button type="button" className="btn secundario" onClick={aoTentarNovamente}>
          Tentar novamente
        </button>
      )}
    </div>
  );
}

export function Vazio({ children }: { children: ReactNode }) {
  return <div className="vazio">{children}</div>;
}

// ── Barra proporcional (frequência, categorias) ────────────────────────────
export function BarraFrequencia({ percentual }: { percentual: number | null }) {
  if (percentual === null) {
    return <span className="texto-nota">isento</span>;
  }
  const cor = percentual >= 70 ? 'var(--gn)' : percentual >= 45 ? 'var(--am)' : 'var(--rd)';
  return (
    <div className="freq-bar">
      <div className="fb-wrap">
        <div className="fb-fill" style={{ width: `${percentual}%`, background: cor }} />
      </div>
      <span style={{ fontSize: 12, color: 'var(--t2)' }}>{porcentagem(percentual)}</span>
    </div>
  );
}

export function BarraCategoria({
  rotulo,
  valor,
  cor,
  larguraPercentual,
  percentualDoTotal,
}: {
  rotulo: string;
  valor: string | number;
  cor: string | null;
  larguraPercentual: number;
  percentualDoTotal?: number;
}) {
  const tom = cor ?? '#6b7280';
  return (
    <div className="barra-cat">
      <div className="barra-topo">
        <span style={{ fontWeight: 500 }}>{rotulo}</span>
        <span style={{ display: 'flex', gap: 10, fontSize: 12, whiteSpace: 'nowrap' }}>
          <span style={{ fontWeight: 600, color: tom }}>{moeda(valor)}</span>
          {percentualDoTotal !== undefined && (
            <span style={{ color: 'var(--t3)' }}>{Math.round(percentualDoTotal)}%</span>
          )}
        </span>
      </div>
      <div className="barra-trilho">
        <div
          className="barra-preenchida"
          style={{ width: `${Math.max(2, larguraPercentual)}%`, background: tom }}
        />
      </div>
    </div>
  );
}

// ── Célula de mensalidade mensal ───────────────────────────────────────────
export function CelulaMes({
  valorPago,
  valorDevido,
  rotulo,
}: {
  valorPago: string | number;
  valorDevido: string | number;
  rotulo: string;
}) {
  const pago = paraNumero(valorPago);
  const devido = paraNumero(valorDevido);
  // Mês sem nada a pagar (isenção, antes do ingresso) não é dívida nem quitação.
  if (devido <= 0 && pago <= 0) {
    return (
      <span className="mc mc-futuro" title={`${rotulo}: nada devido`}>
        —
      </span>
    );
  }
  // 90% do devido conta como quitado: arredondamentos e tarifas do extrato não
  // caracterizam inadimplência.
  const classe = pago >= devido * 0.9 ? 'mc-pago' : pago > 0 ? 'mc-parcial' : 'mc-aberto';
  const simbolo = pago >= devido * 0.9 ? '✓' : pago > 0 ? '½' : '✗';
  return (
    <span className={`mc ${classe}`} title={`${rotulo}: ${moeda(pago)}`}>
      {simbolo}
    </span>
  );
}

// ── Modal genérico ─────────────────────────────────────────────────────────
/** Modais abertos, do mais antigo ao de cima: o Esc fecha só o de cima. */
const modaisAbertos: symbol[] = [];

export function Modal({
  aberto,
  aoFechar,
  children,
  larguraMaxima = 740,
}: {
  aberto: boolean;
  aoFechar: () => void;
  children: ReactNode;
  larguraMaxima?: number;
}) {
  // Esc fecha e o fundo para de rolar enquanto o modal está aberto — sem isso
  // a roda do mouse rola a tabela por trás e o usuário perde onde estava.
  // `aoFechar` costuma vir como arrow inline; o ref evita refazer o efeito a cada render.
  const fechar = useRef(aoFechar);
  fechar.current = aoFechar;
  useEffect(() => {
    if (!aberto) return;
    // Modal aberto de dentro de outro (a visita no perfil do irmão): sem a
    // pilha, um Esc fechava os dois de uma vez.
    const eu = Symbol('modal');
    modaisAbertos.push(eu);
    const teclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && modaisAbertos[modaisAbertos.length - 1] === eu) fechar.current();
    };
    const anterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', teclar);
    return () => {
      document.body.style.overflow = anterior;
      window.removeEventListener('keydown', teclar);
      modaisAbertos.splice(modaisAbertos.indexOf(eu), 1);
    };
  }, [aberto]);

  if (!aberto) return null;
  return (
    <div
      className="overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) aoFechar();
      }}
      role="presentation"
    >
      <div className="modal" style={{ maxWidth: larguraMaxima }} role="dialog" aria-modal="true">
        {children}
      </div>
    </div>
  );
}

// ── Linha rótulo/valor ─────────────────────────────────────────────────────
export function Linha({
  rotulo,
  children,
  cor,
}: {
  rotulo: ReactNode;
  children: ReactNode;
  cor?: string;
}) {
  return (
    <div className="prow">
      <span className="pl">{rotulo}</span>
      <span className="pv" style={cor ? { color: cor } : undefined}>
        {children}
      </span>
    </div>
  );
}

// ── Trio de estatísticas ───────────────────────────────────────────────────
export function Estatistica({
  valor,
  rotulo,
  cor,
  tamanho,
}: {
  valor: ReactNode;
  rotulo: ReactNode;
  cor?: string;
  tamanho?: number;
}) {
  return (
    <div className="s3">
      <div className="s3-v" style={{ color: cor, fontSize: tamanho }}>
        {valor}
      </div>
      <div className="s3-l">{rotulo}</div>
    </div>
  );
}
